import type {
  DemographicFilterQuery,
  IDemographicFilterTab,
  QuestionDemographicDimension,
} from '@brushfire/types'
import { resolveDemographicSegmentDisplayLabel } from '@brushfire/types'

import {
  CHILDREN_OPTIONS,
  EDUCATION_OPTIONS,
  EMPLOYMENT_OPTIONS,
  GENDER_OPTIONS,
  HOUSING_OPTIONS,
  HOUSEHOLD_OPTIONS,
  INDUSTRY_OPTIONS,
  MARITAL_OPTIONS,
  US_STATES,
} from '@/features/instruments/publish/target-audience/lib/demographicFilterCatalog'

export interface DemographicFilterOption {
  value: string
  label: string
}

const AGE_OPTIONS: DemographicFilterOption[] = [
  { value: '18-24', label: '18–24' },
  { value: '25-34', label: '25–34' },
  { value: '35-44', label: '35–44' },
  { value: '45-54', label: '45–54' },
  { value: '55-64', label: '55–64' },
  { value: '65_plus', label: '65+' },
]

const DEVICE_OPTIONS: DemographicFilterOption[] = [
  { value: 'mobile', label: 'Mobile' },
  { value: 'web', label: 'Web' },
]

export const DIMENSION_FIELD_LABELS: Record<QuestionDemographicDimension, string> = {
  age: 'Age',
  gender: 'Gender',
  region: 'Location',
  city: 'City',
  education: 'Education',
  employment: 'Employment',
  deviceType: 'Device type',
  maritalStatus: 'Marital status',
  householdSize: 'Household size',
  numberOfChildren: 'Number of children',
  industry: 'Industry',
  housingStatus: 'Housing status',
}

const DIMENSION_OPTIONS: Record<QuestionDemographicDimension, DemographicFilterOption[]> = {
  age: AGE_OPTIONS,
  gender: GENDER_OPTIONS,
  region: US_STATES,
  city: [],
  education: EDUCATION_OPTIONS,
  employment: EMPLOYMENT_OPTIONS,
  deviceType: DEVICE_OPTIONS,
  maritalStatus: MARITAL_OPTIONS,
  householdSize: HOUSEHOLD_OPTIONS,
  numberOfChildren: CHILDREN_OPTIONS,
  industry: INDUSTRY_OPTIONS,
  housingStatus: HOUSING_OPTIONS,
}

const FILTER_DIMENSIONS: QuestionDemographicDimension[] = [
  'age',
  'gender',
  'region',
  'city',
  'education',
  'employment',
  'deviceType',
  'maritalStatus',
  'householdSize',
  'numberOfChildren',
  'industry',
  'housingStatus',
]

function isQuestionDimension(
  id: IDemographicFilterTab['id'],
): id is QuestionDemographicDimension {
  return FILTER_DIMENSIONS.includes(id as QuestionDemographicDimension)
}

/** Publish-time targeting dimensions from dashboard summary (excludes aggregate `all` tab). */
export function publishTimeFilterDimensions(
  tabs: IDemographicFilterTab[] | undefined,
): QuestionDemographicDimension[] {
  if (!tabs?.length) return []
  return tabs
    .map((tab) => tab.id)
    .filter(isQuestionDimension)
}

/**
 * Resolves filter dimensions for Add Filter visibility and overlay field ordering.
 * Prefers publish-time tab dimensions; falls back to demographicFilterCatalog keys when
 * filterTabs is only the aggregate `all` tab (BFP-680 AC-4).
 */
export function resolveFilterDimensions(
  tabs: IDemographicFilterTab[] | undefined,
  catalog?: DemographicFilterQuery,
): QuestionDemographicDimension[] {
  const fromTabs = publishTimeFilterDimensions(tabs)
  if (fromTabs.length > 0) return fromTabs

  if (!catalog) return []

  return FILTER_DIMENSIONS.filter((dimension) => {
    const values = catalog[dimension]
    return values != null && values.length > 0
  })
}

export function optionsForDemographicDimension(
  dimension: QuestionDemographicDimension,
): DemographicFilterOption[] {
  return DIMENSION_OPTIONS[dimension] ?? []
}

export function hasActiveDemographicFilters(filters: DemographicFilterQuery): boolean {
  return FILTER_DIMENSIONS.some((dimension) => {
    const value = filters[dimension]
    return value != null && value.length > 0
  })
}

function readFilterValue(
  filters: DemographicFilterQuery,
  dimension: QuestionDemographicDimension,
): string | undefined {
  return filters[dimension]?.[0]
}

export function formatDemographicFilterChipLabel(
  dimension: QuestionDemographicDimension,
  value: string,
): string {
  const prefix = DIMENSION_FIELD_LABELS[dimension]
  const display = resolveDemographicSegmentDisplayLabel(dimension, value)
  return `${prefix}: ${display}`
}

export function listDemographicFilterChips(
  filters: DemographicFilterQuery,
): Array<{ dimension: QuestionDemographicDimension; value: string; label: string }> {
  const chips: Array<{
    dimension: QuestionDemographicDimension
    value: string
    label: string
  }> = []

  for (const dimension of FILTER_DIMENSIONS) {
    const value = readFilterValue(filters, dimension)
    if (!value) continue
    chips.push({
      dimension,
      value,
      label: formatDemographicFilterChipLabel(dimension, value),
    })
  }

  return chips
}

export function setDemographicFilterValue(
  filters: DemographicFilterQuery,
  dimension: QuestionDemographicDimension,
  value: string | null,
): DemographicFilterQuery {
  const next = { ...filters }
  if (!value) {
    delete next[dimension]
    return next
  }
  next[dimension] = [value]
  return next
}

export function clearDemographicFilters(): DemographicFilterQuery {
  return {}
}

/** Serialize active filters for instrument dashboard GET query params (comma-separated). */
export function serializeDemographicFilterQuery(
  filters: DemographicFilterQuery,
): Record<string, string> {
  const params: Record<string, string> = {}
  for (const dimension of FILTER_DIMENSIONS) {
    const values = filters[dimension]
    if (values != null && values.length > 0) {
      params[dimension] = values.join(',')
    }
  }
  return params
}
